import _ from 'lodash';
import moment from 'moment-timezone';
import 'twix';

var MOVE_DIRECTION = {
    BACKWARD: 'backward',
    FORWARD: 'forward'
};

var instance = null;

function DateRangeHelperService(dialBackHours) {
    this.dialBackHours = dialBackHours;
    if (instance !== null) {
        throw new Error(
            'Cannot instantiate more than one DatetimeRangeService. ' +
            'Call DatetimeRangeService.getInstance() instead!'
        );
    }
}

function getHumanizedLabel(range) {
    var humanizedLabel = '';
    var start = _.get(range, 'startDate');
    var end = _.get(range, 'endDate');
    if (!_.isNil(start) && !_.isNil(end)) {
        humanizedLabel = start.twix(end).humanizeLength();
    }
    return humanizedLabel;
}

DateRangeHelperService.prototype = {
    MOVE_DIRECTION: MOVE_DIRECTION,

    getFormattedDuration: function (currentRange) {
        var formattedLabel = '';

        if (!_.isEmpty(currentRange)) {
            formattedLabel = getHumanizedLabel(currentRange);
        }

        return formattedLabel;
    },

    getFormattedRange: function (currentRange) {
        var formattedLabel = '';

        if (!_.isEmpty(currentRange)) {
            var start = _.get(currentRange, 'startDate');
            var end = _.get(currentRange, 'endDate');

            if (!_.isNil(start) && !_.isNil(end)) {
                formattedLabel = start.twix(end).format({ hourFormat: 'HH', implicitDate: true });
            }
        }

        return formattedLabel;
    },

    getDefaultRanges: function () {
        var self = this;
        return {
            'Last Hour': self.rangeOfHoursInPastAndCurrentTime(1),

            'Last 4 Hours': self.rangeOfHoursInPastAndCurrentTime(4),

            'Last 8 Hours': self.rangeOfHoursInPastAndCurrentTime(8),

            'Last 12 Hours': self.rangeOfHoursInPastAndCurrentTime(12),

            'Last Day': self.rangeOfHoursInPastAndCurrentTime(24),

            'Last 2 Days': self.rangeOfHoursInPastAndCurrentTime(48)
        };
    },


    /**
     * Returns an array of two elements:
     * Array[0] = the time at N hours in the past
     * Array[1] = the current time.
     * In both entries, the minutes are rounded up to the nearest 5min interval.
     *
     * @param hours the number of hours in the past wanted
     * @returns {*[]}
     */
    rangeOfHoursInPastAndCurrentTime: function(hours) {
        var self = this;
        var start = moment().clone();
        var end = start.clone();
        var delta = self.nextFiveMinuteBoundary(start.minutes());
        return [
            start.subtract(hours, 'hour').set('minute', delta),
            end.set('minute', delta)
            ];
    },

    /**
     * Determines the next 5-minute boundary. We round the minutes up to the
     * nearest 5-minute boundary. If 'minutes' is already on a 5-minute boundry,
     * the minutes stay where they are.
     *
     * @param minutes
     * @returns {*}
     */
    nextFiveMinuteBoundary: function(minutes) {
        var delta = minutes % 5;
        if (delta === 0) // if the minutes are on a 5min boundary already, don't move forward
            return minutes;
        return minutes + (5 - delta);
    },

    getDurationFromRange: function (range) {
        return range.endDate.diff(range.startDate);
    },

    /**
     * Returns the default configuration of the datepicker for Assurance (CPM).
     */
    getLimits: function () {
        let minDate;
        if (this.dialBackHours) {
          minDate = moment().subtract(this.dialBackHours, 'hours');
        } else {
          minDate = moment().subtract(29, 'days').startOf('day');
        }
        return {
            maxDate: moment().endOf('day'), // default fromDate to end-of-current-day

            // We set minDate to prevent the user from navigating the calendar to any date
            // earlier than minDate.
            minDate: minDate,
            timePicker24Hour: true,     // display hours as 0..23
            timePickerIncrement: 5,     // limit user to picking 5-minute intervals
            linkedCalendars: true,      // when true, the to/from calendars are sequential months
            autoApply: true,            // automatically apply a new date range as soon as two dates are clicked,
            alwaysShowCalendars: false,
            autoUpdateInput: true
        };
    },

    getMomentFromUtcFormattedString: function (utcFormattedString) {
        moment.tz.setDefault(moment.tz.guess());
        return moment(utcFormattedString, 'YYYY-MM-DDTHH:mm:ss.SSSZ');
    },

    getRangeFromNowByDuration: function (durationInMs) {
        return {
            startDate: moment().subtract(durationInMs, 'milliseconds'),
            endDate: moment()
        };
    },

    getRoundedHourDifference: function(date) {
        var diff = (moment().diff(date, 'hours', true)).toFixed(2);
        if (diff < 0) diff = 0;
        return diff;
    },

    isNextRangeValid: function (range, direction) {
        var self = this;
        var isNextRangeValid;

        if (_.isEmpty(range)) {
            isNextRangeValid = false;

        } else {
            var limits = self.getLimits();
            var start = moment(range.startDate);
            var end = moment(range.endDate);
            var diffInMs = Math.max(end.diff(start), 1000*60);

            if (direction === MOVE_DIRECTION.BACKWARD) {
                start.subtract(diffInMs, 'milliseconds');
                end.subtract(diffInMs, 'milliseconds');

            } else if (direction === MOVE_DIRECTION.FORWARD) {
                start.add(diffInMs, 'milliseconds');
                end.add(diffInMs, 'milliseconds');
            }

            isNextRangeValid = start.isSameOrAfter(limits.minDate, 'minute') && end.isSameOrBefore(limits.maxDate, 'minute');
        }

        return isNextRangeValid;
    },

    //  TODO - improve: DRY!
    isValidRangeLabel: function (rangeLabel) {
        return _.includes([
            'Last Hour',
            'Last 4 Hours',
            'Last Day',
            'Last 2 Days',
        ], rangeLabel);
    },

    moveTimeRange: function (currentRange, direction) {
        var self = this;
        var start = currentRange.startDate;
        var end = currentRange.endDate;
        var diffInMs = Math.max(end.diff(start), 1000*60);

        if (direction === MOVE_DIRECTION.BACKWARD && self.isNextRangeValid(currentRange, direction)) {
            start.subtract(diffInMs, 'milliseconds');
            end.subtract(diffInMs, 'milliseconds');
            currentRange = {
                startDate: start,
                endDate: end,
                rangeLabel: currentRange.rangeLabel
            };
        } else if (direction === MOVE_DIRECTION.FORWARD && self.isNextRangeValid(currentRange, direction)) {
            start.add(diffInMs, 'milliseconds');
            end.add(diffInMs, 'milliseconds');
            currentRange = {
                startDate: start,
                endDate: end,
                rangeLabel: currentRange.rangeLabel
            };
        }

        return currentRange;
    }
};

export default DateRangeHelperService;
